import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { Observable, combineLatest, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  FileCenterService,
  FileDetailViewModel,
} from '../../common-api/file-center-api';
import { AppContext } from '../../common/public-api';
import {
  FileDisplayInfo,
  FileSyncData,
} from '../../shared/components/file-upload/file-upload.component';
import { ChangeStatusEnum } from '../enums/change-status.enum';
import { FileInfo } from '../models/file-info';
import { HintDialogService } from '../public-api';
import { UserProfileService } from './user-profile.service';

/**
 * 檔案傳輸服務
 *
 * @export
 * @class FileTransferService
 */
@Injectable({
  providedIn: 'root',
})
export class FileTransferService {
  constructor(
    private httpClient: HttpClient,
    private fileCenterService: FileCenterService,
    private userProfileService: UserProfileService,
    private appContext: AppContext,
    private hintDialogService: HintDialogService
  ) {}

  get basePath(): string {
    return this.appContext.envConfig?.apiUrls?.['fileCenter'];
  }

  /**
   * 從指定網址下載檔案(POST)
   *
   * @param {string} url 指定網址
   * @param {object} searchParams POST 參數
   * @param {string} [fileName] 儲存的檔案名稱
   * @return {*}  {Observable<void>}
   * @memberof FileTransferService
   */
  downloadFile(
    url: string,
    searchParams: object,
    fileName?: string
  ): Observable<void> {
    const promise = new Promise<void>((resolve, reject) => {
      this.httpClient
        .post(url, searchParams, {
          responseType: 'blob' as 'json',
          observe: 'response',
        })
        .pipe(
          map((res) => {
            return {
              fileName: fileName ?? this.getFileName(res),
              data: res.body as any,
            };
          })
        )
        .subscribe({
          next: (x) => {
            const blob = new Blob([x.data], {
              type: 'application/octet-stream',
            }); // application後面接下載的副檔名
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = x.fileName; // 瀏覽器下載時的檔案名稱
            link.click();
            window.URL.revokeObjectURL(downloadURL);
            link.remove(); // remove the element
            resolve();
          },
          error: (err) => reject(err),
        });
    });
    return from(promise);
  }

  /**
   * 從指定網址下載檔案(GET)
   *
   * @param {string} url 指定網址
   * @param {string} [fileName] 儲存的檔案名稱
   * @return {*}  {Observable<void>}
   * @memberof FileTransferService
   */
  downloadFileGet(url: string, fileName?: string): Observable<void> {
    const promise = new Promise<void>((resolve, reject) => {
      this.httpClient
        .get(url, { responseType: 'blob' as 'json', observe: 'response' })
        .pipe(
          map((res) => {
            return {
              fileName: fileName ?? this.getFileName(res),
              data: res.body as any,
            };
          })
        )
        .subscribe({
          next: (x) => {
            const blob = new Blob([x.data], {
              type: 'application/octet-stream',
            }); // application後面接下載的副檔名
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = x.fileName; // 瀏覽器下載時的檔案名稱
            link.click();
            window.URL.revokeObjectURL(downloadURL);
            link.remove(); // remove the element
            resolve();
          },
          error: (err) => reject(err),
        });
    });
    return from(promise);
  }
  /**
   * 取得檔案下載url
   *
   * @param {string} url 指定網址
   * @param {string} [fileName] 儲存的檔案名稱
   * @return {*}  {Observable<void>}
   * @memberof FileTransferService
   */
  async downloadFileToUrl(fileId?: string): Promise<string> {
    const url = this.getFileCenterDownloadUrl(fileId!);
    let returnLink: string = '';
    await new Promise<void>((resolve, reject) => {
      this.httpClient
        .get(url, { responseType: 'blob', observe: 'response' })
        .pipe(
          map((res) => {
            return {
              data: res.body as any,
            };
          })
        )
        .subscribe({
          next: (x) => {
            const blob = new Blob([x.data], {
              type: 'application/pdf',
            });
            if (returnLink) {
              window.URL.revokeObjectURL(returnLink);
            }
            returnLink = URL.createObjectURL(blob);
            resolve();
          },
          error: (err) => reject(err),
        });
    });

    return returnLink;
  }

  /**
   * 下載暫存於瀏覽器上的檔案
   *
   * @param {FileInfo} fileInfo 檔案資訊
   * @memberof FileTransferService
   */
  downloadTempFile(fileInfo: FileInfo): Observable<void> {
    const promise = new Promise<void>((resolve, reject) => {
      try {
        const link = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(fileInfo.file as File);
        link.href = downloadUrl;
        link.download =
          `${fileInfo.fileName}${fileInfo.extension}` ?? 'Untitled';
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
        link.remove();
        resolve();
      } catch (e: any) {
        reject(e);
      }
    });
    return from(promise);
  }

  /**
   * 從檔案中心下載檔案
   *
   * @param {string} fileId 檔案 ID
   * @param {string} [fileName] 檔案名稱
   * @return {*}  {Observable<void>}
   * @memberof FileTransferService
   */
  downloadFileFromFileCenter(
    fileId: string,
    fileName?: string
  ): Observable<void> {
    const url = this.getFileCenterDownloadUrl(fileId);
    return this.downloadFileGet(url, fileName);
  }

  /**
   * 取得檔案中心內指定檔案的下載連結
   *
   * @param {string} fileId 檔案 ID
   * @return {*}  {string}
   * @memberof FileTransferService
   */
  getFileCenterDownloadUrl(fileId: string): string {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set('fileDetailId', fileId);
    queryParameters = queryParameters.set(
      'downloader',
      this.userProfileService.userNo ?? ''
    );
    const url = `${
      this.basePath
    }/FileCenter/GetFileDownloadAsync?${queryParameters.toString()}`;

    return url;
  }

  /**
   * 批次上傳檔案至檔案中心
   *
   * @param {*} domain 大分類
   * @param {*} subDomain 小分類
   * @param {FileInfo[]} uploadFiles 上傳檔案清單
   * @return {*}  {Observable<any[]>}
   * @memberof FileTransferService
   */
  uploadFileToFileCenter(
    domain: string | undefined,
    subDomain: string | undefined,
    uploadFiles: FileDisplayInfo[]
  ): Observable<any[]> {
    const userNo = this.userProfileService.userNo;
    const observer = combineLatest(
      uploadFiles.map((fileInfo, idx) =>
        this.fileCenterService
          .fileCenterPostFilesUploadAsyncPostForm(
            domain,
            subDomain,
            fileInfo.description,
            idx,
            false,
            userNo,
            fileInfo.file
          )
          .pipe(
            catchError((error) => {
              this.hintDialogService.showErrorHint(
                'Library.FileError.FileError'
              );
              return throwError(() => error);
            })
          )
      )
    ).pipe(
      catchError((error) => {
        this.hintDialogService.showErrorHint('Library.FileError.FileError');
        return throwError(() => error);
      })
    );

    return observer;
  }

  /**
   * 批次更新檔案資訊至檔案中心
   *
   * @param {FileInfo[]} updateFiles 更新檔案清單
   * @return {*}  {Observable<any[]>}
   * @memberof FileTransferService
   */
  updateFileToFileCenter(updateFiles: FileInfo[]): Observable<any[]> {
    const userNo = this.userProfileService.userNo;
    const observer = combineLatest(
      updateFiles.map((fileInfo) =>
        this.fileCenterService.fileCenterPutFilesPut({
          fileDetailId: fileInfo.fileId,
          fileName: fileInfo.fileName,
          description: fileInfo.description,
          editor: userNo,
        })
      )
    );
    return observer;
  }

  /**
   * 批次刪除檔案中心的檔案
   *
   * @param {FileInfo[]} deleteFiles 刪除檔案清單
   * @return {*}  {Observable<any[]>}
   * @memberof FileTransferService
   */
  deleteFileInFileCenter(deleteFiles: FileInfo[]): Observable<any[]> {
    const observer = combineLatest(
      deleteFiles.map((fileInfo) => {
        const jsonStr = JSON.stringify(fileInfo.fileId);
        return this.fileCenterService.fileCenterDeleteFileDelete(jsonStr);
      })
    );
    return observer;
  }

  /**
   * 取得檔案中心指定檔案資訊清單
   *
   * @param {string[]} fileIds 指定檔案 ID 列表
   * @return {*}  {Observable<FileDetailViewModel[]>}
   * @memberof FileTransferService
   */
  getFileInfosFromFileCenter(
    fileIds: string[]
  ): Observable<FileDetailViewModel[]> {
    return this.fileCenterService
      .fileCenterPostReturnFileDetailListPost(fileIds)
      .pipe(
        // default: sort files with edit time (=upload time) in ascending order
        map((result: FileDetailViewModel[]) => sortBy(result, ['editTime']))
      );
  }

  /**
   * 取得檔案中心單筆檔案資訊
   *
   * @param {string[]} fileId 指定檔案 ID
   * @return {*}  {Promise<FileDetailViewModel>}
   * @memberof FileTransferService
   */
  async getCurrentFile(fileId: string): Promise<FileDetailViewModel> {
    return new Promise((resolve, reject) => {
      this.fileCenterService.fileCenterGetFileDetailGet(fileId).subscribe(
        (fileRes: FileDetailViewModel) => {
          console.log('getCurrentFile', fileRes);
          resolve(fileRes);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getCurrentFileUrl(fileId: string): string {
    let url: any;
    new Promise((resolve, reject) => {
      this.fileCenterService.fileCenterGetFileDetailGet(fileId).subscribe(
        (fileRes: FileDetailViewModel) => {
          console.log('getCurrentFile', fileRes);
          resolve(fileRes);
          url = fileRes.fullPath!;
        },
        (error) => {
          reject(error);
        }
      );
    });

    return url;
  }

  /**
   * 從 Response Header 取得檔案名稱
   *
   * @private
   * @param {HttpResponse<object>} res Http Response
   * @return {*}  {*}
   * @memberof FileTransferService
   */
  private getFileName(res: HttpResponse<object>): any {
    const contentDisposition = res.headers.get('content-disposition');
    if (contentDisposition) {
      const fileName = contentDisposition
        .split(';')[1]
        .trim()
        .split('filename=')[1];
      return fileName;
    } else {
      return undefined;
    }
  }

  // --------- 以下方法擷取自 file-upload.component.ts ------------

  /**
   * 同步至檔案中心
   *
   * @param {FileSyncData} syncData
   * @return {*}  {Observable<any>}
   * @memberof FileUploadComponent
   */
  syncToFileCenter(syncData: FileSyncData): Observable<any> {
    const tasks: any[] = [];
    if (syncData.newFiles?.length > 0) {
      tasks.push(
        this.uploadFileToFileCenter(
          syncData.domain,
          syncData.subDomain,
          syncData.updateFiles
        )
      );
    }
    if (syncData.updateFiles?.length > 0) {
      tasks.push(this.updateFileToFileCenter(syncData.updateFiles));
    }
    if (syncData.deleteFiles?.length > 0) {
      tasks.push(this.deleteFileInFileCenter(syncData.deleteFiles));
    }
    const observable =
      tasks.length > 0
        ? combineLatest(tasks).pipe(
            map<any[], any[]>((results) =>
              results.reduce((acc, item) => acc.concat(item ?? []), [])
            )
          )
        : new Observable((observer) => observer.next([]));

    return observable;
  }

  easySyncToFileCenter(
    files: FileDisplayInfo[],
    deletedFiles: FileDisplayInfo[],
    domain: string,
    subDomain: string
  ): Observable<any> {
    const syncData: FileSyncData = {
      domain,
      subDomain,
      newFiles: files?.filter((x) => x.status === ChangeStatusEnum.added),
      updateFiles: files?.filter((x) => x.status === ChangeStatusEnum.modified),
      deleteFiles: deletedFiles?.filter((x) => !x.isTemp),
    };

    return this.syncToFileCenter(syncData);
  }
}
